import {
  Box,
  Button,
  Card,
  ExternalIcon,
  Flex,
  Loading,
  SearchIcon,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import type { FlexProps } from '@iheartjane/reefer';
import { useMaskito } from '@maskito/react';
import { clsx } from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import { useFetchIframeHeader } from '@jane/business-admin/data-access';
import { urlMask } from '@jane/business-admin/util';
import { isValidUrl } from '@jane/shared-ecomm/util';
import { normalizeUrl } from '@jane/shared/util';

import styles from './premiumIframePreview.module.css';

interface BorderedFlexProps extends FlexProps {
  /** Hide the section without unmounting it, so any fields inside keep their state */
  hide?: boolean;
}

export const BorderedFlex = ({
  className,
  hide,
  ...props
}: BorderedFlexProps) => (
  <Flex
    className={clsx(styles['bordered'], className)}
    // `display` is set inline rather than by class so that it also applies in
    // jsdom, where the stylesheet isn't loaded and hidden fields would otherwise
    // stay in the accessibility tree.
    style={hide ? { display: 'none' } : undefined}
    {...props}
  />
);

const DEFAULT_URL = 'https://';

export const PremiumIframePreview = () => {
  const urlRef = useMaskito({ options: urlMask });
  const [retailerUrl, setRetailerUrl] = useState(DEFAULT_URL);
  const [showPreview, setShowPreview] = useState(false);
  const [isiFrameLoading, setIsiFrameLoading] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const { mutateAsync: fetchIframeHeader } = useFetchIframeHeader();

  const onUrlChange = (value: string) => {
    setShowPreview(false);
    const normalizedUrl = normalizeUrl(value);
    setRetailerUrl(normalizedUrl);
  };

  const validateIframeUrl = useCallback(
    async (url: string) => {
      setIsiFrameLoading(true);

      const result = await fetchIframeHeader(url);
      const { ok, header } = result;
      if (ok && !header) {
        setIframeError(false);
        setIsiFrameLoading(false);
        return;
      }

      setIframeError(true);
    },
    [fetchIframeHeader]
  );

  useEffect(() => {
    if (showPreview) {
      validateIframeUrl(retailerUrl);
    }
  }, [showPreview, validateIframeUrl, retailerUrl]);

  // Handle enter key to show the preview instead of submitting the Colors form
  useEffect(() => {
    const inputElement = document.querySelector<HTMLInputElement>(
      'input[name="retailer_url"]'
    );
    if (!inputElement) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (isValidUrl(retailerUrl)) {
          setShowPreview(true);
        }
      }
    };

    inputElement.addEventListener('keydown', handleKeyDown);
    return () => {
      inputElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [retailerUrl]);

  return (
    <>
      <BorderedFlex py={40} justifyContent="center">
        <Box maxWidth={780} width="100%">
          <Typography variant="body-bold">Main website source</Typography>
          <Typography color="grays-mid" mt={12}>
            Provide the URL of your main website to directly match the color
            palette
          </Typography>
          <Flex alignItems="center">
            <TextField
              ref={urlRef}
              label=""
              name="retailer_url"
              defaultValue={retailerUrl}
              onChange={onUrlChange}
              mb={12}
              mr={12}
            />
            <Button
              variant="secondary"
              label="Preview website"
              startIcon={<SearchIcon />}
              disabled={!isValidUrl(retailerUrl)}
              onClick={() => setShowPreview(true)}
            />
          </Flex>
        </Box>
      </BorderedFlex>

      <BorderedFlex
        alignItems="center"
        flexDirection="column"
        grow={2}
        background="grays-ultralight"
        p={64}
      >
        {!showPreview && (
          <Card className={styles['styledCard']}>
            <Card.Content>
              <Flex flexDirection="column" alignItems="center" px={24} py={24}>
                <Typography branded variant="header-bold">
                  Don't know your brand colors?
                </Typography>
                <Typography mt={8} textAlign="center">
                  Enter your URL for your main website above to directly
                  reference the colors. If you don't see your site appear, click
                  to open it in a new window.
                </Typography>
              </Flex>
            </Card.Content>
          </Card>
        )}

        {showPreview && (
          <Card width={iframeError ? '512px' : '100%'}>
            <Card.Content>
              {iframeError && (
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  px={24}
                  py={24}
                >
                  <Typography branded variant="header-bold">
                    Source preview error
                  </Typography>
                  <Typography mt={8} textAlign="center">
                    The URL you’ve entered encountered an error or doesn’t allow
                    iframe previews. You can still view the website for color
                    sourcing in another window.
                  </Typography>
                  <Button
                    label="View website"
                    full
                    endIcon={<ExternalIcon />}
                    mt={24}
                    onClick={() =>
                      window.open(retailerUrl, '_blank', 'noopener,noreferrer')
                    }
                  />
                </Flex>
              )}

              {!iframeError && (
                <>
                  {isiFrameLoading && <Loading color="black" />}
                  <iframe
                    src={retailerUrl}
                    title="Preview"
                    width="100%"
                    height="720px"
                    style={{ border: 0 }}
                  />
                </>
              )}
            </Card.Content>
          </Card>
        )}
      </BorderedFlex>
    </>
  );
};
